import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Button,
  Checkbox,
  Image,
  Input,
  Slider,
  Radio,
  Progress,
  Spin,
  message,
  Result,
} from 'antd';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CheckCircleFilled,
  FormOutlined,
  UserOutlined,
  KeyOutlined,
} from '@ant-design/icons';
import PagesWrapper from '../../PagesWrapper';
import { useAuth } from '../../context/AuthContext';
import surveysService, { ChoiceType } from '../../services/surveysService';
import styles from './Survey.module.css';

const { TextArea } = Input;

const Survey = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const questionContainerRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [survey, setSurvey] = useState(null);
  const [error, setError] = useState(null);

  // Participation state
  const [participantIdentifier, setParticipantIdentifier] = useState('');
  const [hasStarted, setHasStarted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [participantId, setParticipantId] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);

  const fetchSurvey = useCallback(async () => {
    try {
      setLoading(true);
      const data = await surveysService.getPublicSurvey(id);
      setSurvey(data);
    } catch (error) {
      if (error.response?.status === 404) {
        setError('not_found');
      } else if (error.response?.status === 403) {
        setError('not_published');
      } else {
        setError('load_error');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchSurvey();
  }, [fetchSurvey]);

  // Scroll to top of question when navigating between questions
  useEffect(() => {
    if (hasStarted && questionContainerRef.current) {
      questionContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentQuestionIndex, hasStarted]);

  const handleStart = async () => {
    if (!survey.allowAnonymous && !participantIdentifier.trim()) {
      message.warning(t('surveys.enterIdentifier', 'Please enter your identifier'));
      return;
    }

    try {
      setSubmitting(true);
      const result = await surveysService.startParticipation(id, {
        participantIdentifier: participantIdentifier.trim() || '',
      });
      
      // Handle error response from backend
      if (!result.success) {
        if (result.errorCode === 'IP_RATE_LIMIT_EXCEEDED') {
          message.error(t('surveys.ipRateLimitExceeded', 'Maximum participations from this location reached. Please try again later.'));
        } else if (result.errorCode === 'ANONYMOUS_NOT_ALLOWED') {
          message.error(t('surveys.anonymousNotAllowed', 'This survey requires login to participate.'));
        } else {
          message.error(result.errorMessage || t('surveys.startError', 'Failed to start survey'));
        }
        return;
      }
      
      setParticipantId(result.participantId);
      setHasStarted(true);
    } catch (error) {
      // Handle 400 error responses
      const errorData = error.response?.data;
      if (errorData?.errorCode === 'IP_RATE_LIMIT_EXCEEDED') {
        message.error(t('surveys.ipRateLimitExceeded', 'Maximum participations from this location reached. Please try again later.'));
      } else if (errorData?.errorCode === 'ANONYMOUS_NOT_ALLOWED') {
        message.error(t('surveys.anonymousNotAllowed', 'This survey requires login to participate.'));
      } else {
        message.error(errorData?.message || t('surveys.startError', 'Failed to start survey'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleAnswerChange = (questionId, value, choiceId = null, choiceType = null, isMultiple = false) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        choiceId,
        choiceIds: isMultiple ? value : null, // For multi-select, value is array of IDs
        choiceType,
        value: isMultiple ? value : value,
        isMultiple,
      },
    }));
  };

  const getCurrentQuestion = () => {
    if (!survey || !survey.questions) return null;
    return survey.questions[currentQuestionIndex];
  };

  const canProceed = () => {
    const question = getCurrentQuestion();
    if (!question) return true;
    if (!question.isRequired) return true;
    
    const answer = answers[question.id];
    if (!answer) return false;
    
    // Check if ANY type of answer is provided:
    // 1. Normal choices (single or multiple) - AllowMultipleChoices applies here
    const hasNormalAnswer = answer.isMultiple 
      ? (answer.choiceIds && answer.choiceIds.length > 0)
      : (answer.choiceId != null);
    
    // 2. Range choices - any range value answered
    const hasRangeAnswer = answer.rangeValues && Object.keys(answer.rangeValues).length > 0;
    
    // 3. FreeText choices - any non-empty text value
    const hasFreeTextAnswer = answer.textValues && 
      Object.values(answer.textValues).some(v => v && v.trim().length > 0);
    
    // For required questions, at least one type of answer must be provided
    return hasNormalAnswer || hasRangeAnswer || hasFreeTextAnswer;
  };

  const handleNext = async () => {
    // Just move to next question - answers are collected in state
    // and will be submitted all at once when finishing
    if (currentQuestionIndex < survey.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      // Complete the survey
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleComplete = async () => {
    try {
      setSubmitting(true);
      
      // Build all answers from state - a question may have multiple choice types
      const allAnswers = [];
      
      Object.entries(answers).forEach(([questionId, answer]) => {
        const qId = parseInt(questionId);
        
        // Handle normal choices (single or multiple select)
        if (answer.choiceId || (answer.choiceIds && answer.choiceIds.length > 0)) {
          allAnswers.push({
            questionId: qId,
            choiceId: answer.isMultiple ? undefined : answer.choiceId,
            choiceIds: answer.isMultiple ? answer.choiceIds : undefined,
          });
        }
        
        // Handle range choices - each range choice generates an answer
        if (answer.rangeValues) {
          Object.entries(answer.rangeValues).forEach(([choiceId, value]) => {
            allAnswers.push({
              questionId: qId,
              choiceId: parseInt(choiceId),
              numericValue: value,
            });
          });
        }
        
        // Handle freetext choices - each freetext choice generates an answer
        if (answer.textValues) {
          Object.entries(answer.textValues).forEach(([choiceId, value]) => {
            if (value && value.trim()) {  // Only submit non-empty text
              allAnswers.push({
                questionId: qId,
                choiceId: parseInt(choiceId),
                textValue: value,
              });
            }
          });
        }
      });
      
      await surveysService.submitSurveyResponse(id, participantId, allAnswers);
      setIsCompleted(true);
    } catch (error) {
      console.error('Failed to complete survey:', error);
      message.error(t('surveys.completeError', 'Failed to complete survey'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    // Just move to next question without adding an answer
    if (currentQuestionIndex < survey.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      handleComplete();
    }
  };

  const renderIntro = () => (
    <div className={styles.surveyContent}>
      <div className={styles.participationCard}>
        {user && (
          <div className={styles.userInfoSection}>
            <div className={styles.userInfoHeader}>
              <Avatar
                size={44}
                src={user.profileImageUrl}
                icon={!user.profileImageUrl && <UserOutlined />}
                className={styles.userAvatar}
              />
              <div className={styles.userInfoDetails}>
                <span className={styles.userInfoName}>
                  {user.firstName && user.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user.email}
                </span>
                <span className={styles.userInfoEmail}>{user.email}</span>
              </div>
            </div>
          </div>
        )}

        <div className={styles.surveyKeySection}>
          <div className={styles.surveyKeyHeader}>
            <div className={styles.surveyKeyLabel}>
              <KeyOutlined className={styles.surveyKeyIcon} />
              <span>{t('surveys.surveyKey', 'Survey Key')}</span>
              {!survey.allowAnonymous && <span className={styles.requiredMark}>*</span>}
            </div>
            <span className={styles.surveyKeyHint}>
              {survey.allowAnonymous
                ? t('surveys.optionalKey', 'You may optionally enter a key to identify your responses')
                : t('surveys.requiredKey', 'Please enter your survey key to participate')}
            </span>
          </div>
          <Input
            prefix={<KeyOutlined style={{ color: '#bfbfbf' }} />}
            placeholder={t('surveys.keyPlaceholder', 'Enter your survey key')}
            value={participantIdentifier}
            onChange={(e) => setParticipantIdentifier(e.target.value)}
            className={styles.surveyKeyInput}
            size="large"
          />
        </div>

        <Button
          type="primary"
          size="large"
          block
          onClick={handleStart}
          loading={submitting}
          className={styles.startButton}
          icon={<FormOutlined />}
        >
          {t('surveys.startSurvey', 'Start Survey')}
        </Button>
      </div>
    </div>
  );

  const renderQuestion = () => {
    const question = getCurrentQuestion();
    if (!question) return null;

    const answer = answers[question.id];
    const hasChoices = question.choices && question.choices.length > 0;

    return (
      <div className={styles.questionCard}>
        <div className={styles.questionHeader}>
          <span className={styles.questionName}>
            {question.name}
            {question.isRequired && <span className={styles.questionRequired}>*</span>}
          </span>
        </div>

        <div className={styles.questionContent}>
          {question.imageUrl && (
            <Image src={question.imageUrl} alt="" className={styles.questionImage} />
          )}
          <p className={styles.questionBody}>{question.body}</p>
        </div>

        {hasChoices && renderChoices(question, answer)}
        
        {!hasChoices && (
          <p style={{ color: '#999', fontStyle: 'italic' }}>
            {t('surveys.noChoices', 'No choices available for this question')}
          </p>
        )}

        {!question.isRequired && (
          <Button
            className={styles.skipButton}
            onClick={handleSkip}
          >
            {t('surveys.skip', 'Skip this question')}
          </Button>
        )}
      </div>
    );
  };

  // Helper to determine choice type
  const getChoiceType = (choice) => {
    const raw = choice?.choiceType;
    const str = String(raw).toLowerCase();
    if (raw === 1 || str === 'range' || str === '1') return 'range';
    if (raw === 2 || str === 'freetext' || str === '2') return 'freetext';
    return 'normal';
  };

  // Render all choices for a question - each choice rendered by its own type, preserving order
  const renderChoices = (question, answer) => {
    const choices = question.choices || [];
    const isMultiple = question.allowMultipleChoices;
    const selectedIds = isMultiple ? (answer?.choiceIds || []) : [];

    return (
      <div className={styles.choicesContainer}>
        {isMultiple && (
          <div className={styles.multiSelectHint}>
            <span>{t('surveys.selectMultiple', 'Select all that apply')}</span>
          </div>
        )}
        {choices.map((choice, index) => {
          const choiceType = getChoiceType(choice);
          
          if (choiceType === 'range') {
            return (
              <div key={choice.id} className={index > 0 ? styles.choiceSpacing : undefined}>
                {renderSingleRangeChoice(question, answer, choice)}
              </div>
            );
          }
          
          if (choiceType === 'freetext') {
            return (
              <div key={choice.id} className={index > 0 ? styles.choiceSpacing : undefined}>
                {renderSingleFreeTextChoice(question, answer, choice)}
              </div>
            );
          }
          
          // Normal choice - render as individual selectable option
          return renderSingleNormalChoice(question, answer, choice, isMultiple, selectedIds);
        })}
      </div>
    );
  };

  // Render a single normal choice (radio or checkbox)
  const renderSingleNormalChoice = (question, answer, choice, isMultiple, selectedIds) => {
    const isSelected = isMultiple 
      ? selectedIds.includes(choice.id) 
      : answer?.choiceId === choice.id;

    const handleClick = () => {
      if (isMultiple) {
        const newIds = selectedIds.includes(choice.id)
          ? selectedIds.filter(id => id !== choice.id)
          : [...selectedIds, choice.id];
        handleAnswerChange(question.id, newIds, null, ChoiceType.Normal, true);
      } else {
        handleAnswerChange(question.id, choice.id, choice.id, ChoiceType.Normal, false);
      }
    };

    return (
      <div
        key={choice.id}
        className={`${styles.choiceOption} ${isSelected ? styles.selected : ''}`}
        onClick={handleClick}
      >
        {isMultiple ? (
          <Checkbox checked={isSelected} />
        ) : (
          <Radio checked={isSelected} />
        )}
        <div className={styles.choiceContent}>
          {choice.imageUrl && (
            <Image src={choice.imageUrl} alt="" className={styles.choiceImage} onClick={(e) => e.stopPropagation()} />
          )}
          <span className={styles.choiceText}>{choice.text}</span>
        </div>
      </div>
    );
  };



  // Render a single range choice as slider
  const renderSingleRangeChoice = (question, answer, choice) => {
    // For range, we store the value keyed by choice ID
    const rangeAnswers = answer?.rangeValues || {};
    const min = choice.rangeMin ?? 0;
    const max = choice.rangeMax ?? 100;
    const value = rangeAnswers[choice.id] ?? min;
    const step = choice.rangeStep || 1;
    
    const minLabel = choice.rangeMinLabel || min;
    const maxLabel = choice.rangeMaxLabel || max;
    
    return (
      <div className={styles.rangeContainer}>
        {choice.text && (
          <div className={styles.rangeHeader}>
            <span className={styles.rangeLabel}>{choice.text}</span>
          </div>
        )}
        
       
        <div className={styles.rangeSliderWrapper}>
          <Slider
            min={min}
            max={max}
            step={step}
            value={value}
            tooltip={{ formatter: (val) => val }}
            onChange={(val) => {
              const newRangeValues = { ...(answer?.rangeValues || {}), [choice.id]: val };
              setAnswers(prev => ({
                ...prev,
                [question.id]: {
                  ...prev[question.id],
                  rangeValues: newRangeValues,
                  choiceType: ChoiceType.Range,
                },
              }));
            }}
          />
        </div>
        
        <div className={styles.rangeScale}>
          <div className={styles.rangeScaleLabel}>
            <span className={styles.rangeScaleValue}>{min}</span>
            <span className={styles.rangeScaleText}>{minLabel !== min ? minLabel : ''}</span>
          </div>
         
          <div className={`${styles.rangeScaleLabel} ${styles.rangeScaleLabelEnd}`}>
            <span className={styles.rangeScaleValue}>{max}</span>
            <span className={styles.rangeScaleText}>{maxLabel !== max ? maxLabel : ''}</span>
          </div>
        </div>
      </div>
    );
  };

  // Render a single free text choice as text area
  const renderSingleFreeTextChoice = (question, answer, choice) => {
    // For freetext, we store values keyed by choice ID
    const textAnswers = answer?.textValues || {};
    const value = textAnswers[choice.id] || '';
    const maxChars = choice.maxCharacters || 1000;
    const charCount = value.length;

    return (
      <div className={styles.freeTextContainer}>
        {choice.text && <p className={styles.freeTextLabel}>{choice.text}</p>}
        <TextArea
          value={value}
          onChange={(e) => {
            const newTextValues = { ...(answer?.textValues || {}), [choice.id]: e.target.value };
            setAnswers(prev => ({
              ...prev,
              [question.id]: {
                ...prev[question.id],
                textValues: newTextValues,
                choiceType: ChoiceType.FreeText,
              },
            }));
          }}
          placeholder={choice.placeholder || t('surveys.enterResponse', 'Enter your response...')}
          rows={4}
          maxLength={maxChars}
        />
        <div className={`${styles.charCount} ${charCount > maxChars * 0.9 ? (charCount >= maxChars ? styles.error : styles.warning) : ''}`}>
          {charCount} / {maxChars}
        </div>
      </div>
    );
  };

  const renderCompletion = () => (
    <div className={styles.completionSection}>
      <CheckCircleFilled className={styles.completionIcon} />
      <h2 className={styles.completionTitle}>{t('surveys.thankYou', 'Thank You!')}</h2>
      {survey.finishText ? (
        <p className={styles.completionMessage}>{survey.finishText}</p>
      ) : (
        <p className={styles.completionMessage}>
          {t('surveys.defaultFinish', 'Your responses have been recorded. Thank you for participating!')}
        </p>
      )}
      <Button
        type="primary"
        onClick={() => navigate('/')}
        style={{ marginTop: 24 }}
      >
        {t('surveys.backToHome', 'Back to Home')}
      </Button>
    </div>
  );

  if (loading) {
    return (
      <PagesWrapper showHeader={false}>
        <div className={styles.surveyPage}>
          <div className={styles.surveyContainer}>
            <div className={styles.loadingContainer}>
              <Spin size="large" />
            </div>
          </div>
        </div>
      </PagesWrapper>
    );
  }

  if (error) {
    return (
      <PagesWrapper showHeader={false}>
        <div className={styles.surveyPage}>
          <div className={styles.surveyContainer}>
            <Result
              status={error === 'not_found' ? '404' : error === 'not_published' ? '403' : 'error'}
              title={
                error === 'not_found' 
                  ? t('surveys.notFound', 'Survey not found')
                  : error === 'not_published'
                  ? t('surveys.notPublished', 'This survey is not currently available')
                  : t('surveys.loadError', 'Failed to load survey')
              }
              extra={
                <Button type="primary" onClick={() => navigate('/')}>
                  {t('surveys.backToHome', 'Back to Home')}
                </Button>
              }
            />
          </div>
        </div>
      </PagesWrapper>
    );
  }

  if (!survey) {
    return null;
  }

  const totalQuestions = survey.questions?.length || 0;
  const progress = hasStarted ? Math.round(((currentQuestionIndex + 1) / totalQuestions) * 100) : 0;

  return (
    <PagesWrapper showHeader={false}>
      <div className={styles.surveyPage}>
      <div className={styles.surveyContainer}>
        {/* Landing page header - image left, text right */}
        {!hasStarted && (
          <div className={styles.surveyLanding}>
            {survey.imageUrl && (
            <div className={styles.surveyLandingImage}>
              <Image
                src={survey.imageUrl}
                alt={survey.name}
                className={styles.surveyImage}
                preview={false}
                fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Crect fill='%23f0f2f5' width='200' height='200'/%3E%3C/svg%3E"
              />
            </div>
          )}
            <div className={styles.surveyLandingContent}>
              <h1 className={styles.surveyTitle}>{survey.name}</h1>
              {survey.introText && (
                <p className={styles.surveyIntro}>{survey.introText}</p>
              )}
              {totalQuestions > 0 && (
                <div className={styles.surveyMeta}>
                  <span className={styles.surveyMetaItem}>
                    <FormOutlined /> {t('surveys.questionCount', '{{count}} questions', { count: totalQuestions })}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Minimal header when playing questions */}
        {hasStarted && !isCompleted && (
          <>
            <div className={styles.surveyPlayingHeader}>
              <h2 className={styles.surveyPlayingTitle}>{survey.name}</h2>
            </div>
            <div className={styles.progressSection}>
              <div className={styles.progressText}>
                <span>{t('surveys.questionProgress', 'Question {{current}} of {{total}}', { current: currentQuestionIndex + 1, total: totalQuestions })}</span>
                <span>{progress}%</span>
              </div>
              <Progress percent={progress} showInfo={false} strokeColor="#1890ff" />
            </div>
          </>
        )}

        {isCompleted ? (
          renderCompletion()
        ) : !hasStarted ? (
          renderIntro()
        ) : (
          <div className={styles.surveyContent} ref={questionContainerRef}>
            {renderQuestion()}

            <div className={styles.navigationButtons}>
              <Button
                onClick={handlePrevious}
                disabled={currentQuestionIndex === 0}
                icon={<ArrowLeftOutlined />}
              >
                {t('common.previous', 'Previous')}
              </Button>

              <Button
                type="primary"
                onClick={handleNext}
                disabled={!canProceed()}
                loading={submitting}
              >
                {currentQuestionIndex === totalQuestions - 1 
                  ? t('surveys.finish', 'Finish')
                  : t('common.next', 'Next')
                }
                {currentQuestionIndex < totalQuestions - 1 && <ArrowRightOutlined />}
              </Button>
            </div>
          </div>
        )}
        </div>
      </div>
    </PagesWrapper>
  );
};

export default Survey;
